/**
 * LAB-106: structured tables in Copilot answers.
 *
 * The agent returns comparisons either as markdown tables - often malformed: no
 * outer pipes, every row squashed onto one line, wrapped in a code fence - or as
 * Adaptive Card attachments. Both are turned into well-formed markdown tables here,
 * so one renderer (formatCopilotMessage) draws them and the table survives being
 * saved to chat history as plain text.
 */

export type TableAlignment = "left" | "center" | "right" | undefined

const SEPARATOR_CELL = /^:?-{3,}:?$/

/**
 * Split on unescaped pipes; `\|` becomes a literal pipe inside the token.
 * (Hand-rolled rather than a lookbehind regex, which older Safari cannot parse.)
 */
function splitOnPipes(value: string): string[] {
  const tokens: string[] = []
  let current = ""
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i]
    if (char === "\\" && value[i + 1] === "|") {
      current += "|"
      i += 1
    } else if (char === "|") {
      tokens.push(current)
      current = ""
    } else {
      current += char
    }
  }
  tokens.push(current)
  return tokens
}

/** Drop the empty tokens produced by a row's outer pipes. */
function withoutOuterPipes(tokens: string[]) {
  const result = [...tokens]
  if (result.length > 1 && result[0].trim() === "") result.shift()
  if (result.length > 1 && result[result.length - 1].trim() === "") result.pop()
  return result
}

export function splitTableCells(line: string): string[] {
  return withoutOuterPipes(splitOnPipes(line.trim())).map((cell) => cell.trim())
}

export function isTableSeparatorRow(line: string) {
  if (!line.includes("-")) return false
  const cells = splitTableCells(line)
  return cells.length > 1 && cells.every((cell) => SEPARATOR_CELL.test(cell))
}

export function tableAlignments(separator: string): TableAlignment[] {
  return splitTableCells(separator).map((cell) => {
    const left = cell.startsWith(":")
    const right = cell.endsWith(":")
    if (left && right) return "center"
    if (right) return "right"
    if (left) return "left"
    return undefined
  })
}

function hasUnescapedPipe(line: string) {
  return /(^|[^\\])\|/.test(line)
}

function toRow(cells: string[]) {
  return `| ${cells.map((cell) => cell.replace(/\|/g, "\\|")).join(" | ")} |`
}

function separatorCell(cell = "") {
  const value = cell.trim()
  const left = value.startsWith(":") ? ":" : ""
  const right = value.length > 1 && value.endsWith(":") ? ":" : ""
  return `${left}---${right}`
}

/** A separator row for `width` columns, keeping any `:---:` alignment given. */
function separatorRow(width: number, cells: string[] = []) {
  return `| ${Array.from({ length: width }, (_, index) =>
    separatorCell(cells[index])
  ).join(" | ")} |`
}

/** Index of every pipe that is not escaped as `\|`. */
function pipeIndexes(line: string) {
  const indexes: number[] = []
  for (let i = 0; i < line.length; i += 1) {
    if (line[i] === "|" && line[i - 1] !== "\\") indexes.push(i)
  }
  return indexes
}

/**
 * A whole table on one line, often after a lead-in sentence:
 * "Here is the comparison: | A | B | |---|---| | x | y |". The separator gives the
 * column count, so the table part splits into rows of that width; text before
 * and after it stays as paragraphs. Returns null when the line does not fit that
 * shape exactly, so ordinary text with pipes is never mangled.
 */
function expandSquashedTable(line: string): string[] | null {
  const separator = /\|(\s*:?-{3,}:?\s*\|)+/.exec(line)
  if (!separator) return null
  const columns = (separator[0].match(/-{3,}/g) || []).length
  if (columns < 2) return null

  const pipes = pipeIndexes(line)
  const before = line.slice(0, pipes[0]).trim()
  const after = line.slice(pipes[pipes.length - 1] + 1).trim()
  const tokens = withoutOuterPipes(
    splitOnPipes(line.slice(pipes[0], pipes[pipes.length - 1] + 1))
  )

  // Rows of `columns` cells, each followed by the empty token between "| |".
  const rows: string[][] = []
  for (let i = 0; i < tokens.length; i += columns + 1) {
    const row = tokens.slice(i, i + columns)
    if (row.length !== columns) return null
    const filler = tokens[i + columns]
    if (filler !== undefined && filler.trim() !== "") return null
    rows.push(row.map((cell) => cell.trim()))
  }
  if (rows.length < 3) return null
  if (!rows[1].every((cell) => SEPARATOR_CELL.test(cell))) return null
  if (rows.some((row, index) => index !== 1 && row.every((cell) => !cell)))
    return null
  return [
    ...(before ? [before, ""] : []),
    ...rows.map((row, index) =>
      index === 1 ? separatorRow(columns, row) : toRow(row)
    ),
    ...(after ? ["", after] : []),
  ]
}

/**
 * Rewrite every table in `text` as a well-formed markdown table: one row per
 * line, outer pipes, header + separator first. Anything that is not a table is
 * returned unchanged.
 */
export function normalizeMarkdownTables(text: string): string {
  const unfenced = text.replace(
    /```[a-z]*\n([\s\S]*?)\n```/gi,
    (block, inner: string) => {
      const lines = inner.split("\n")
      return lines.some(isTableSeparatorRow) ? inner : block
    }
  )

  const lines = unfenced.replace(/\r\n/g, "\n").split("\n")
  const out: string[] = []
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    const squashed = hasUnescapedPipe(line) ? expandSquashedTable(line) : null
    if (squashed) {
      out.push(...squashed)
    } else if (
      isTableSeparatorRow(line) &&
      out.length &&
      hasUnescapedPipe(out[out.length - 1]) &&
      !isTableSeparatorRow(out[out.length - 1])
    ) {
      // Header row + separator: everything up to the next blank or pipe-less line
      // is a body row, with or without outer pipes.
      const header = splitTableCells(out.pop() as string)
      out.push(
        toRow(header),
        separatorRow(header.length, splitTableCells(line))
      )
      while (
        i + 1 < lines.length &&
        lines[i + 1].trim() &&
        hasUnescapedPipe(lines[i + 1])
      ) {
        i += 1
        out.push(toRow(splitTableCells(lines[i])))
      }
    } else {
      out.push(line)
    }
  }
  return out.join("\n")
}

// ---------------------------------------------------------------------------
// Adaptive Cards

type CardNode = Record<string, any>

const ADAPTIVE_CARD = "application/vnd.microsoft.card.adaptive"
const HERO_CARDS = [
  "application/vnd.microsoft.card.hero",
  "application/vnd.microsoft.card.thumbnail",
]

export type CopilotCardAttachment = {
  contentType?: string
  content?: unknown
  contentUrl?: string
  name?: string
}

function cellText(value: string) {
  return value.replace(/\s*\n\s*/g, "<br>").trim()
}

function inlineText(node: CardNode): string {
  if (!node || typeof node !== "object") return ""
  switch (node.type) {
    case "TextBlock": {
      const value = String(node.text || "").trim()
      return node.weight?.toLowerCase() === "bolder" && value
        ? `**${value}**`
        : value
    }
    case "RichTextBlock":
      return (node.inlines || [])
        .map((inline: CardNode | string) => {
          if (typeof inline === "string") return inline
          const value = String(inline.text || "")
          return inline.weight?.toLowerCase() === "bolder" && value.trim()
            ? `**${value.trim()}**`
            : value
        })
        .join("")
        .trim()
    case "Container":
    case "Column":
    case "TableCell":
      return (node.items || []).map(inlineText).filter(Boolean).join("\n")
    case "ColumnSet":
      return (node.columns || []).map(inlineText).filter(Boolean).join(" ")
    case "FactSet":
      return (node.facts || [])
        .map((fact: CardNode) => `${fact.title || ""}: ${fact.value || ""}`)
        .join("\n")
    case "Image":
      return node.altText ? String(node.altText) : ""
    default:
      return typeof node.fallbackText === "string" ? node.fallbackText : ""
  }
}

function tableFromRows(rows: string[][]): string {
  const width = Math.max(...rows.map((row) => row.length))
  const padded = rows.map((row) =>
    Array.from({ length: width }, (_, index) => cellText(row[index] || ""))
  )
  return [
    toRow(padded[0]),
    separatorRow(width),
    ...padded.slice(1).map(toRow),
  ].join("\n")
}

/** A ColumnSet holding only text - the way cards often lay out one table row. */
function textOnlyColumns(node: CardNode): string[] | null {
  if (
    node.type !== "ColumnSet" ||
    !Array.isArray(node.columns) ||
    node.columns.length < 2
  ) {
    return null
  }
  const textual = node.columns.every((column: CardNode) =>
    (column.items || []).every((item: CardNode) =>
      ["TextBlock", "RichTextBlock"].includes(item.type)
    )
  )
  return textual ? node.columns.map(inlineText) : null
}

function blocksFromElements(elements: CardNode[] = []): string[] {
  const blocks: string[] = []
  for (let i = 0; i < elements.length; i += 1) {
    const node = elements[i]
    if (!node || typeof node !== "object" || node.isVisible === false) {
      // hidden or invalid elements are skipped, as a card host would
    } else if (node.type === "Table") {
      // A cell's "type": "TableCell" is optional in the schema, so read its items directly.
      const rows: string[][] = (node.rows || []).map((row: CardNode) =>
        (row.cells || []).map((cell: CardNode) =>
          (cell?.items || []).map(inlineText).filter(Boolean).join("\n")
        )
      )
      const headed =
        node.firstRowAsHeader === false && Array.isArray(node.columns)
          ? [node.columns.map(() => ""), ...rows]
          : rows
      if (headed.length >= 2) blocks.push(tableFromRows(headed))
      else if (rows.length) blocks.push(rows[0].join(" · "))
    } else if (node.type === "FactSet") {
      const facts = (node.facts || []).map((fact: CardNode) => [
        String(fact.title || ""),
        String(fact.value || ""),
      ])
      if (facts.length)
        blocks.push(tableFromRows([["Attribute", "Value"], ...facts]))
    } else if (textOnlyColumns(node)) {
      // Consecutive same-width text ColumnSets are a hand-built table.
      const rows = [textOnlyColumns(node) as string[]]
      while (
        i + 1 < elements.length &&
        textOnlyColumns(elements[i + 1])?.length === rows[0].length
      ) {
        i += 1
        rows.push(textOnlyColumns(elements[i]) as string[])
      }
      blocks.push(rows.length >= 2 ? tableFromRows(rows) : rows[0].join(" · "))
    } else if (["Container", "Column"].includes(node.type)) {
      blocks.push(...blocksFromElements(node.items))
    } else if (node.type === "ColumnSet") {
      ;(node.columns || []).forEach((column: CardNode) => {
        blocks.push(...blocksFromElements(column.items))
      })
    } else if (node.type === "ActionSet") {
      blocks.push(...actionLinks(node.actions))
    } else if (node.type === "Image" && node.url) {
      // LAB-109: rendered as a picture with its source, not as a bare link.
      blocks.push(imageMarkdown(node.url, node.altText))
    } else if (node.type === "ImageSet") {
      blocks.push(
        ...(node.images || [])
          .filter((image: CardNode) => image?.url)
          .map((image: CardNode) => imageMarkdown(image.url, image.altText))
      )
    } else {
      const value = inlineText(node)
      if (value) blocks.push(value)
    }
  }
  return blocks
}

/** Parentheses and spaces would end a markdown link early; encodeURIComponent keeps "(" and ")". */
export function markdownUrl(url: string) {
  return url
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/\s/g, "%20")
}

function imageMarkdown(url: string, alt?: string) {
  const text = String(alt || "Image").replace(/[[\]]/g, "").trim() || "Image"
  return `![${text}](${markdownUrl(String(url))})`
}

function actionLinks(actions: CardNode[] = []): string[] {
  const links = actions
    .filter((action) => action?.type === "Action.OpenUrl" && action.url)
    .map((action) => `- [${action.title || action.url}](${action.url})`)
  return links.length ? [links.join("\n")] : []
}

export function adaptiveCardToMarkdown(card: unknown): string {
  if (!card || typeof card !== "object") return ""
  const node = card as CardNode
  const blocks = [
    ...blocksFromElements(node.body),
    ...actionLinks(node.actions),
  ]
  const text = blocks.filter(Boolean).join("\n\n").trim()
  return (
    text || (typeof node.fallbackText === "string" ? node.fallbackText : "")
  )
}

function heroCardToMarkdown(card: unknown): string {
  if (!card || typeof card !== "object") return ""
  const node = card as CardNode
  return [
    node.title ? `**${node.title}**` : "",
    node.subtitle || "",
    node.text || "",
    ...(node.images || [])
      .filter((image: CardNode) => image?.url)
      .map((image: CardNode) => imageMarkdown(image.url, image.alt)),
    ...actionLinks(
      (node.buttons || []).map((button: CardNode) => ({
        type: button.type === "openUrl" ? "Action.OpenUrl" : button.type,
        title: button.title,
        url: button.value,
      }))
    ),
  ]
    .filter(Boolean)
    .join("\n\n")
}

/**
 * Markdown for the cards and https images on an agent message; other attachment types are
 * ignored.
 */
export function cardAttachmentsToMarkdown(
  attachments: CopilotCardAttachment[] = []
) {
  return attachments
    .map((attachment) => {
      const type = (attachment?.contentType || "").toLowerCase()
      if (type.startsWith("image/")) {
        return /^https:\/\//i.test(attachment.contentUrl || "")
          ? imageMarkdown(attachment.contentUrl as string, attachment.name)
          : ""
      }
      let content = attachment?.content
      if (typeof content === "string") {
        try {
          content = JSON.parse(content)
        } catch {
          return ""
        }
      }
      if (type === ADAPTIVE_CARD) return adaptiveCardToMarkdown(content)
      if (HERO_CARDS.includes(type)) return heroCardToMarkdown(content)
      return ""
    })
    .filter(Boolean)
    .join("\n\n")
}
